import type React from "react";
import type { NamedOption } from "./types";
import { helperClass, inputClass, labelClass, sectionCard } from "./constants";

type CategoriesSectionProps = {
  categories: NamedOption[];
  categoryId: number | null;
  setCategoryId: (id: number | null) => void;
  categoryIds: number[];
  setCategoryIds: React.Dispatch<React.SetStateAction<number[]>>;
  showCategoryInput: boolean;
  setShowCategoryInput: React.Dispatch<React.SetStateAction<boolean>>;
  newCategoryName: string;
  setNewCategoryName: (name: string) => void;
  handleAddNewCategory: () => void;
  toggleCategory: (id: number) => void;
};

export const CategoriesSection = ({
  categories,
  categoryId,
  setCategoryId,
  categoryIds,
  setCategoryIds,
  showCategoryInput,
  setShowCategoryInput,
  newCategoryName,
  setNewCategoryName,
  handleAddNewCategory,
  toggleCategory,
}: CategoriesSectionProps) => (
  <section className={sectionCard}>
    <h2 className="text-xl font-semibold text-[#5c2f2f] mb-4">Categories</h2>

    <div className="grid grid-cols-1 gap-5">
      <div>
        <div className="flex items-center justify-between gap-3">
          <label className={labelClass}>Categories</label>
          <button
            type="button"
            onClick={() => setShowCategoryInput((prev) => !prev)}
            className="text-sm font-semibold text-[#5c2f2f] hover:underline"
          >
            + Add New
          </button>
        </div>

        <div className="mt-3">
          <label className={labelClass}>Primary Category</label>
          <select
            value={categoryId ?? ""}
            onChange={(e) => {
              const nextCategoryId = Number(e.target.value);
              setCategoryId(Number.isFinite(nextCategoryId) ? nextCategoryId : null);
              setCategoryIds((prev) =>
                Number.isFinite(nextCategoryId)
                  ? Array.from(new Set([nextCategoryId, ...prev]))
                  : prev
              );
            }}
            className={inputClass}
          >
            <option value="">Select primary category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {showCategoryInput && (
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              placeholder="New category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className={inputClass}
            />
            <button
              type="button"
              onClick={handleAddNewCategory}
              className="px-4 py-3 rounded-xl bg-[#5c2f2f] text-white hover:bg-[#7a4444]"
            >
              Save
            </button>
          </div>
        )}

        <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {categories.map((category) => (
              <label
                key={category.id}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={categoryIds.includes(category.id)}
                  onChange={() => toggleCategory(category.id)}
                  className="h-4 w-4 rounded border-gray-300 text-[#5c2f2f] focus:ring-[#5c2f2f]"
                />
                <span>{category.name}</span>
              </label>
            ))}
          </div>
          <p className={helperClass}>
            Check every category this product belongs to. The primary category is
            used as the default category across older product views.
          </p>
        </div>
      </div>
    </div>
  </section>
);
